// OAuth Configuration and Social Login Service
class OAuthService {
  constructor() {
    this.config = null;
    this.kakaoInitialized = false;
    // 포트 기반 환경 감지: 8443 포트는 배포환경 테스트용
    this.isDevelopment = process.env.NODE_ENV === 'development' && window.location.port !== '8443';
  }

  // OAuth 설정 가져오기
  async getOAuthConfig() {
    if (this.config) {
      return this.config;
    }

    try {
      // 프록시를 통해 백엔드 API 호출
      const response = await fetch('/api/config');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON');
      }
      
      this.config = await response.json();
      return this.config;
    } catch (error) {
      console.error('Error fetching OAuth config:', error);
      
      // 기본 설정으로 폴백 - 모든 환경에서 React 콜백 사용
      const redirectUri = `${window.location.origin}/oauth/callback`;
      
      this.config = {
        kakaoJsKey: process.env.REACT_APP_KAKAO_JS_KEY || '',
        redirectUri: redirectUri,
        naverClientId: process.env.REACT_APP_NAVER_CLIENT_ID || '',
        naverRedirectUri: redirectUri,
        googleClientId: '',
        googleRedirectUri: '',
        oauth: {
          google: {
            enabled: false,
            mode: 'gis_id_token',
            clientId: ''
          }
        },
        featureFlags: {
          showNaverLogin: false,
          showAppleLogin: false
        }
      };
      
      return this.config;
    }
  }

  // Kakao 초기화
  async initializeKakao() {
    if (this.kakaoInitialized) {
      return;
    }

    try {
      const config = await this.getOAuthConfig();
      
      if (window.Kakao && config.kakaoJsKey) {
        window.Kakao.init(config.kakaoJsKey);
        this.kakaoInitialized = true;
        console.log('Kakao initialized successfully');
      }
    } catch (error) {
      console.error('Failed to initialize Kakao:', error);
    }
  }

  // Kakao 로그인
  async kakaoLogin() {
    try {
      const config = await this.getOAuthConfig();

      if (!config.kakaoJsKey) {
        throw new Error('카카오 로그인이 설정되지 않았습니다.');
      }

      const redirectUri = `${window.location.origin}/oauth/callback`;

      const state = `provider_kakao_${this.generateRandomString()}`;
      sessionStorage.setItem('oauth_state', state);
      localStorage.setItem('oauth_state', state);
      await this.registerState(state);

      const params = new URLSearchParams({
        client_id: config.kakaoJsKey,
        redirect_uri: redirectUri,
        response_type: 'code',
        state: state,
      });

      window.location.href = `https://kauth.kakao.com/oauth/authorize?${params.toString()}`;
    } catch (error) {
      console.error('Kakao login error:', error);
      throw error;
    }
  }

  // Naver 로그인
  async naverLogin() {
    try {
      const config = await this.getOAuthConfig();
      
      if (!config.naverClientId) {
        throw new Error('Naver OAuth not configured');
      }

      // React 앱의 OAuth 콜백 경로로 리다이렉트
      const redirectUri = `${window.location.origin}/oauth/callback`;

      // State 생성 및 sessionStorage에 저장 (CSRF 방지)
      const state = `provider_naver_${this.generateRandomString()}`;
      sessionStorage.setItem('oauth_state', state);
      localStorage.setItem('oauth_state', state);
      await this.registerState(state);

      const params = new URLSearchParams({
        client_id: config.naverClientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        state: state
      });

      const oauthUrl = `https://nid.naver.com/oauth2.0/authorize?${params.toString()}`;
      window.location.href = oauthUrl;
    } catch (error) {
      console.error('Naver login error:', error);
      throw error;
    }
  }

  // Google 로그인
  async googleLogin() {
    throw new Error('Google 로그인은 공식 Google Identity Services 버튼에서 시작해주세요.');
  }

  // 소셜 계정 연결 (로그인된 상태에서 추가 계정 연결)
  async linkAccount(provider) {
    if (provider === 'google') {
      throw new Error('Google 계정 연결은 설정 화면의 공식 Google 버튼을 사용해주세요.');
    }
    const config = await this.getOAuthConfig();
    const redirectUri = `${window.location.origin}/oauth/callback`;

    // link 모드 마커: OAuthCallback에서 읽어 Authorization 헤더 추가
    const state = `link_${provider}_${this.generateRandomString()}`;
    sessionStorage.setItem('oauth_state', state);
    localStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_link_mode', 'true');
    await this.registerState(state);

    let oauthUrl;
    switch (provider) {
      case 'kakao': {
        const clientId = config.kakaoJsKey;
        if (!clientId) throw new Error('Kakao OAuth not configured');
        const p = new URLSearchParams({
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: 'code',
          state,
        });
        oauthUrl = `https://kauth.kakao.com/oauth/authorize?${p}`;
        break;
      }
      case 'naver': {
        if (!config.naverClientId) throw new Error('Naver OAuth not configured');
        const p = new URLSearchParams({
          client_id: config.naverClientId,
          redirect_uri: redirectUri,
          response_type: 'code',
          state,
        });
        oauthUrl = `https://nid.naver.com/oauth2.0/authorize?${p}`;
        break;
      }
      case 'google': {
        throw new Error('Google 계정 연결은 설정 화면의 공식 Google 버튼을 사용해주세요.');
      }
      default:
        throw new Error(`Unsupported provider: ${provider}`);
    }

    window.location.href = oauthUrl;
  }

  // 기능 플래그 적용
  getFeatureFlags() {
    if (!this.config || !this.config.featureFlags) {
      return {
        showNaverLogin: false,
        showAppleLogin: false
      };
    }
    return this.config.featureFlags;
  }

  // Kakao SDK 로드 상태 확인
  isKakaoSDKLoaded() {
    return typeof window.Kakao !== 'undefined';
  }

  // 현재 환경 확인
  getEnvironment() {
    return this.isDevelopment ? 'development' : 'production';
  }

  // M-2: provider 리다이렉트 전에 state를 백엔드에 등록.
  // 백엔드가 동일 state를 HttpOnly 쿠키로 심어, 콜백에서 서버 측 CSRF 검증에 사용한다.
  // 반드시 리다이렉트(window.location.href) 이전에 await 하여 Set-Cookie가 반영되게 한다.
  async registerState(state) {
    const response = await fetch('/api/oauth/state', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ state }),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('보안 검증 준비에 실패했습니다. 다시 시도해주세요.');
    }
  }

  // 랜덤 문자열 생성 (state 파라미터용)
  generateRandomString(length = 16) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const values = new Uint8Array(length);
    if (!window.crypto?.getRandomValues) {
      throw new Error('안전한 로그인 요청을 생성할 수 없는 브라우저입니다.');
    }
    window.crypto.getRandomValues(values);
    return Array.from(values, value => chars.charAt(value % chars.length)).join('');
  }
}

const oauthService = new OAuthService();
export default oauthService;
